import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ImageClassifier.css';

const API_BASE = 'https://imdb-sentiment-backend.onrender.com/api/image-classifier';

function ImageClassifier() {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
      setResult(null); // Clear previous results
    }
  };

  const handleRandomImage = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/random`);
      const data = await res.json();

      if (data.error) {
        setResult({ error: data.error });
      } else {
        setPreviewUrl(data.image);
        setSelectedImage('random'); // Mark as random image
        // Store actual class for comparison later
        setResult({ actualClass: data.actual_class });
      }
    } catch (error) {
      setResult({ error: 'Error loading random image. Make sure the server is running.' });
    }
    setLoading(false);
  };

  const handlePredict = async () => {
    if (!selectedImage) {
      setResult({ error: 'Please select an image first' });
      return;
    }

    setLoading(true);
    try {
      let formData = new FormData();

      if (selectedImage === 'random') {
        // Convert base64 to blob for random images
        const response = await fetch(previewUrl);
        const blob = await response.blob();
        formData.append('image', blob, 'random.jpg');
      } else {
        formData.append('image', selectedImage);
      }

      const res = await fetch(`${API_BASE}/predict`, {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (data.error) {
        setResult({ error: data.error });
      } else {
        setResult(prev => ({
          ...data,
          actualClass: prev?.actualClass // Preserve actual class if it was a random image
        }));
      }
    } catch (error) {
      setResult({ error: 'Server error. Please try again.' });
    }
    setLoading(false);
  };

  const clearImage = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
    setResult(null);
  };

  return (
    <>
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2>How This AI Works</h2>

            <div className="explanation-section">
              <h3>Technical Explanation</h3>
              <p>
                <strong>Step 1:</strong> Load and prepare 25,000 labeled images (12,500 cats, 12,500 dogs) from the Kaggle dataset.
                <strong> Step 2:</strong> Resize all images to a consistent dimension (e.g., 150×150 pixels) and normalize pixel values to the range [0, 1].
                <strong> Step 3:</strong> Design a Convolutional Neural Network (CNN) architecture with multiple convolutional layers (detecting edges, textures, shapes), max pooling layers (reducing spatial dimensions), and dense layers (final classification).
                <strong> Step 4:</strong> Apply data augmentation (flipping, rotating, zooming) to increase training variety and reduce overfitting.
                <strong> Step 5:</strong> Add dropout layers to prevent the model from memorizing training images.
                <strong> Step 6:</strong> Train the CNN using binary cross-entropy loss and the Adam optimizer over multiple epochs.
                <strong> Step 7:</strong> Evaluate on test images to achieve 84% accuracy.
                <strong> Step 8:</strong> Deploy via Flask API where uploaded images are preprocessed and classified.
              </p>
            </div>

            <div className="explanation-section simply-put">
              <h3>Simply Put</h3>
              <p>
                The AI learned to tell cats from dogs by looking at 25,000 pictures - 12,500 of each. It started by learning simple things like 'cats have pointy ears' and 'dogs have wet noses,' then learned more complex patterns like face shapes and fur textures. After lots of practice, it got good enough to correctly identify cats and dogs in new pictures about 84% of the time, even if they're in different poses or lighting.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="image-classifier-app">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Back to Portfolio
        </button>
        <button className="info-btn" onClick={() => setShowModal(true)}>
          ℹ️ How It Works
        </button>
        <h1>Cats vs Dogs Image Classifier</h1>
        <p className="subtitle">Upload an image or try a random one from the dataset</p>

      <div className="controls-section">
        <div className="upload-controls">
          <label className="upload-btn">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
            />
            Choose Image
          </label>

          <button
            className="random-btn"
            onClick={handleRandomImage}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Random Image'}
          </button>

          {selectedImage && (
            <button className="clear-btn" onClick={clearImage}>
              Clear
            </button>
          )}
        </div>
      </div>

      {previewUrl && (
        <div className="image-preview">
          <img src={previewUrl} alt="Preview" />
        </div>
      )}

      {selectedImage && (
        <button
          className="predict-btn"
          onClick={handlePredict}
          disabled={loading}
        >
          {loading ? 'Analyzing...' : 'Predict'}
        </button>
      )}

      {result && (
        <div className="result-section">
          {result.error ? (
            <p className="error">{result.error}</p>
          ) : result.prediction ? (
            <>
              <div className="prediction-card">
                <h2>Prediction: {result.prediction}</h2>
                <p className="confidence">
                  Confidence: {(result.confidence * 100).toFixed(2)}%
                </p>

                {result.actualClass && (
                  <p className={`actual-class ${result.prediction === result.actualClass ? 'correct' : 'incorrect'}`}>
                    Actual: {result.actualClass}
                    {result.prediction === result.actualClass ? ' ✓' : ' ✗'}
                  </p>
                )}
              </div>

              <div className="probabilities">
                <h3>Class Probabilities</h3>
                <div className="prob-bar-container">
                  <div className="prob-bar">
                    <span className="prob-label">Cat</span>
                    <div className="bar-wrapper">
                      <div
                        className="bar cat-bar"
                        style={{ width: `${result.probabilities.cats * 100}%` }}
                      />
                      <span className="prob-value">{(result.probabilities.cats * 100).toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className="prob-bar">
                    <span className="prob-label">Dog</span>
                    <div className="bar-wrapper">
                      <div
                        className="bar dog-bar"
                        style={{ width: `${result.probabilities.dogs * 100}%` }}
                      />
                      <span className="prob-value">{(result.probabilities.dogs * 100).toFixed(2)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}

      <hr style={{ margin: '40px 0' }} />

      <div className="model-info" style={{ textAlign: 'left' }}>
        <h2>About This Model</h2>
        <ul>
          <li><strong>Architecture:</strong> Custom CNN with 4 convolutional blocks and batch normalization</li>
          <li><strong>Dataset:</strong> 8,000 cat and dog images (5,600 training, 1,200 validation, 1,200 test)</li>
          <li><strong>Image Size:</strong> 224×224 pixels</li>
          <li><strong>Parameters:</strong> 1.3M trainable parameters</li>
          <li><strong>Accuracy:</strong> 84.33% validation accuracy</li>
          <li><strong>Training:</strong> 50 epochs with data augmentation, learning rate scheduling, and early stopping</li>
        </ul>
        <p style={{ marginTop: '1em' }}>
          The model uses aggressive data augmentation (rotation, zoom, flip, brightness, contrast)
          and advanced techniques like global average pooling and dropout for regularization.
        </p>
      </div>
    </div>
    </>
  );
}

export default ImageClassifier;
